import { EarIcon, ShieldIcon, HandshakeIcon } from 'lucide-react'
import type { PillarPageContent } from '../../types/pillarPage'

export const relacionesPage: PillarPageContent = {
  slug: 'relaciones',
  accent: { base: '198 124 132', deep: '58 33 36', ink: '8 9 9' },
  affiliateUrl: '',

  hero: {
    eyebrow: 'Relaciones · CreceElite',
    headlineTop: 'No fuiste creado para caminar solo.',
    headlineAccent: 'Las relaciones correctas fortalecen tu destino.',
    quote:
      '“Aprende a comunicarte mejor, construir relaciones sanas, liderar con amor y resolver conflictos con sabiduría.”',
    ctaLabel: 'Fortalecer mis relaciones',
    tags: ['Comunicación', 'Confianza', 'Liderazgo', 'Sabiduría'],
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/1372b290-2f84-4c97-9494-32c6d2f226f8.jpg',
    imageAlt: 'Dos personas conversando frente a frente en un espacio oscuro con luz cálida',
  },

  perspective: {
    eyebrow: 'Cambio de perspectiva',
    titleTop: 'No se trata de tener más contactos.',
    titleSub: 'Se trata de construir vínculos que sostienen.',
    paragraphs: [
      'Puedes alcanzar metas, ingresos y reconocimiento. Pero si tus relaciones más cercanas están rotas, ningún logro se sentirá completo.',
      'Las personas con las que caminas influyen en tus decisiones, en tu paz y en la versión de ti mismo que se despierta cada día.',
      'Por eso, en CreceElite no vemos las relaciones como una red de utilidad ni como una lista de personas.',
    ],
    closingParagraph: 'Las vemos como una forma de administración.',
    highlight: ['Cuida tus vínculos.', 'Fortalece tu comunicación.', 'Lidera con amor.'],
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/9a0a2152-d3f3-40aa-ab34-8122433b4eeb.jpg',
    imageAlt: 'Dos manos apoyadas cerca una de la otra sobre una mesa oscura',
    caption: 'Vínculos, no contactos',
  },

  foundation: {
    eyebrow: 'El valor de caminar acompañado',
    title: 'Tus relaciones merecen cuidado porque tu vida se construye con otros.',
    quote:
      '“Mejores son dos que uno... y si el uno cayere, el otro le levantará.”',
    quoteSource: 'Eclesiastés 4:9-10',
    lead: 'El objetivo no es agradar a todo el mundo.',
    closing:
      'El objetivo es construir relaciones sanas, honestas y firmes, capaces de acompañarte en el propósito que Dios puso delante de ti.',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/8eb6c48a-20bd-45c3-8d59-deab1ed61f79.jpg',
    imageAlt: 'Siluetas de una familia caminando juntas hacia una luz cálida',
    virtues: [
      {
        label: 'Escucha',
        text: 'Entender antes de responder, incluso cuando no estás de acuerdo.',
        icon: EarIcon,
      },
      {
        label: 'Límites',
        text: 'Cuidar lo que permites, sin dejar de amar a las personas.',
        icon: ShieldIcon,
      },
      {
        label: 'Compromiso',
        text: 'Sostener el vínculo cuando la emoción del inicio ya no está.',
        icon: HandshakeIcon,
      },
    ],
  },

  pillars: {
    eyebrow: 'Los 3 pilares',
    title: 'Una relación sana se construye sobre tres fundamentos.',
    items: [
      {
        number: '01',
        label: 'Comunicación',
        headline: 'Lo que no se habla se convierte en distancia.',
        text: 'Comunicar no es solamente decir lo que piensas. Es aprender a escuchar, preguntar, expresar lo que sientes con claridad y hablar en el momento adecuado.',
      },
      {
        number: '02',
        label: 'Confianza',
        headline: 'La coherencia construye lo que las palabras prometen.',
        text: 'La confianza se edifica con hechos repetidos: cumplir, estar presente, decir la verdad y sostener tu palabra incluso cuando nadie lo está viendo.',
      },
      {
        number: '03',
        label: 'Conflicto',
        headline: 'Todo vínculo real atraviesa desacuerdos.',
        text: 'El problema no es el conflicto, sino cómo lo enfrentas. Aprender a resolver con sabiduría, sin agresión y sin silencio prolongado, es lo que hace duradera una relación.',
      },
    ],
  },

  purpose: {
    eyebrow: 'El verdadero objetivo',
    title: '¿Para qué quieres relaciones sanas?',
    reasons: [
      { label: 'Para tener paz', text: 'Para vivir sin la carga de vínculos sin resolver.' },
      { label: 'Para tu familia', text: 'Para construir un hogar donde todos puedan crecer.' },
      { label: 'Para tu trabajo', text: 'Para liderar e influir sin manipular ni imponer.' },
      {
        label: 'Para tu futuro',
        text: 'Para llegar a los próximos años acompañado de las personas correctas.',
      },
    ],
    statementLead: 'No cuidas tus relaciones solamente para evitar conflictos.',
    statementAccent: 'Las cuidas para construir la vida que quieres compartir.',
  },

  discipline: {
    eyebrow: 'Disciplina relacional',
    titleTop: 'La afinidad te acerca.',
    titleAccent: 'El carácter te sostiene.',
    paragraphs: [
      'Habrá días en los que preferirás callar antes que hablar.',
      'Días en los que será más fácil alejarte que resolver.',
    ],
    closingParagraph:
      'Pero los vínculos se fortalecen cuando tus respuestas dejan de depender de cómo te sientes en ese momento.',
    sequence: ['Escuchar', 'Comprender', 'Responder', 'Reconstruir'],
    closingLead: 'No necesitas relaciones perfectas.',
    closingAccent: 'Necesitas relaciones cuidadas.',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/84261317-ec36-46bc-813c-eb6b9167c375.jpg',
  },

  path: {
    eyebrow: 'Ahora necesitas un camino',
    title: 'Ahora necesitas un camino.',
    paragraphs: [
      'Reconocer que quieres relacionarte mejor es solamente el comienzo.',
      'El siguiente paso es aprender qué hacer, cómo hacerlo y cómo mantenerlo en el tiempo.',
    ],
    steps: [
      { label: 'Comunicación', text: 'Aprende a expresarte con claridad y sin herir.' },
      { label: 'Escucha', text: 'Desarrolla la capacidad de entender antes de reaccionar.' },
      { label: 'Límites', text: 'Aprende a cuidar tu paz sin romper el vínculo.' },
      { label: 'Conflicto', text: 'Resuelve desacuerdos con sabiduría y no con silencio.' },
      { label: 'Liderazgo', text: 'Influye con amor en tu casa, tu equipo y tu entorno.' },
    ],
  },

  formation: {
    eyebrow: 'El siguiente paso',
    title: 'No tienes que descubrirlo todo solo.',
    paragraphs: [
      'Una de las mejores decisiones que puedes tomar es aprender de personas que ya han recorrido este camino.',
      'Por eso, hemos seleccionado una formación que puede ayudarte a comunicarte mejor, resolver conflictos con sabiduría y construir relaciones más sanas y duraderas.',
    ],
    cardEyebrow: 'Formación recomendada',
    cardTitle: 'Desarrolla tus relaciones',
    cardQuote: '“Escucha. Comunica. Resuelve. Repite.”',
    highlights: ['Escucha.', 'Comunica.', 'Resuelve.', 'Repite.'],
    ctaLabel: 'Conocer el método',
    note: 'Una recomendación de CreceElite para quienes quieren vínculos sanos, no atajos.',
  },

  identity: {
    eyebrow: 'Identidad',
    title: '¿Quién estás decidiendo ser?',
    before: [
      'Reaccionar',
      'Conversaciones evitadas',
      'Límites difusos',
      'Silencio prolongado',
      'Depender de la aprobación',
    ],
    after: ['Escucha', 'Claridad', 'Límites sanos', 'Honestidad', 'Liderazgo con amor'],
    statementLead: 'La transformación de tus relaciones comienza mucho antes de que cambie el otro.',
    statementAccent: 'Comienza cuando cambia tu manera de relacionarte.',
  },

  manifesto: {
    eyebrow: 'Manifiesto CreceElite',
    title: 'Mis relaciones son mi responsabilidad',
    lines: [
      'No espero que el otro cambie primero. Empiezo por mí.',
      'No busco tener razón. Busco entender.',
      'No callo para evitar el conflicto. Hablo para resolverlo.',
      'No confundo amar con permitir todo. Pongo límites sanos.',
      'No busco ser admirado. Quiero ser confiable.',
      'Porque todavía tengo mucho por vivir. Mucho por construir. Mucho por compartir.',
    ],
  },

  finalCta: {
    headlineTop: 'Tus relaciones son el camino.',
    headlineAccent: 'Tu propósito es el destino.',
    paragraph:
      'Ahora que entiendes el peso que tienen tus vínculos, el siguiente paso es desarrollar habilidades que puedas llevar a la práctica cada día.',
    ctaLabel: 'Comenzar mi camino',
    subnote: 'Accede a la formación recomendada por CreceElite.',
  },

  stickyCtaLabel: 'Comenzar mi camino',
}
